import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { HiCheck, HiTruck, HiLocationMarker, HiClock, HiStar, HiEmojiHappy } from 'react-icons/hi';

const API =  process.env.REACT_APP_API_URL;

// Helper to add business days to a date
function addBusinessDays(date, days) {
  let result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    if (result.getDay() !== 0 && result.getDay() !== 6) { // skip weekends
      added++;
    }
  }
  return result;
}

export default function TrackOrder() {
  const { trackingNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [review, setReview] = useState({ rating: 0, comment: '' });
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/track/${trackingNumber}`)
      .then(res => setOrder(res.data))
      .catch(() => setError('Tracking number not found'))
      .finally(() => setLoading(false));
  }, [trackingNumber]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-gray-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <p className="text-2xl font-bold text-gray-900 mb-2">Tracking not found</p>
          <p className="text-gray-500">The tracking number you entered is invalid or has no data yet.</p>
        </div>
      </div>
    );
  }

  const steps = order.history || [];
  const currentStatus = order.currentStatus || 'Unknown';

  // Estimate delivery window: shipped date + 3‑5 business days
  const shippedStep = steps.find(s => s.status === 'Shipped');
  const estimatedStart = shippedStep ? addBusinessDays(new Date(shippedStep.timestamp), 3) : null;
  const estimatedEnd = shippedStep ? addBusinessDays(new Date(shippedStep.timestamp), 5) : null;

  const formatDate = (date) => date.toLocaleDateString('en-NG', { weekday: 'short', month: 'short', day: 'numeric' });

  const handleRating = (star) => {
    setReview({ ...review, rating: star });
  };

  const handleReviewSubmit = () => {
    // Here you would POST the review to your backend.
    // For now, we simulate submission.
    setReviewSubmitted(true);
  };

  const isDelivered = currentStatus === 'Delivered';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto space-y-6">
        
        {/* Delivery success banner (only when delivered) */}
        {isDelivered && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center shadow-sm">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
              <HiCheck className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-bold text-emerald-800">Your order has been delivered!</h2>
            <p className="text-emerald-600 mt-1">Enjoy your new gear from WAVEYYENT.</p>
          </div>
        )}

        {/* Main card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <div className="text-center mb-8">
            <HiTruck className={`text-4xl mx-auto mb-3 ${isDelivered ? 'text-emerald-600' : 'text-gray-900'}`} />
            <h1 className="text-2xl font-bold text-gray-900">Order Tracking</h1>
            <p className="text-sm text-gray-500 mt-1">
              Tracking number: <span className="font-mono font-medium text-gray-700">{trackingNumber}</span>
            </p>
          </div>

          {/* Current Status */}
          <div className={`rounded-xl p-5 mb-8 ${isDelivered ? 'bg-emerald-600' : 'bg-gray-900'} text-white`}>
            <p className="text-sm font-semibold uppercase tracking-wider opacity-70">Current Status</p>
            <p className="text-xl font-bold mt-1">{currentStatus}</p>
          </div>

          {/* Estimated delivery (only when shipped but not yet delivered) */}
          {shippedStep && !isDelivered && estimatedStart && estimatedEnd && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-center">
              <p className="text-sm text-blue-700">
                Estimated delivery: <span className="font-semibold">{formatDate(estimatedStart)} – {formatDate(estimatedEnd)}</span>
              </p>
            </div>
          )}

          {/* Timeline */}
          <div className="flow-root">
            <ul className="-mb-8">
              {steps.map((step, index) => (
                <li key={index}>
                  <div className="relative pb-8">
                    {index < steps.length - 1 && (
                      <span
                        className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                    )}
                    <div className="relative flex items-start space-x-3">
                      <div>
                        <div
                          className={`relative px-1 ${
                            index === steps.length - 1
                              ? isDelivered ? 'bg-emerald-600' : 'bg-green-600'
                              : 'bg-gray-900'
                          } h-8 w-8 rounded-full flex items-center justify-center ring-4 ring-white`}
                        >
                          <HiCheck className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {step.status}
                          </p>
                          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                            <HiLocationMarker className="h-3.5 w-3.5" />
                            <span>{step.location}</span>
                            <HiClock className="h-3.5 w-3.5 ml-1" />
                            <span>{new Date(step.timestamp).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Review prompt (only when delivered) */}
        {isDelivered && !reviewSubmitted && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <HiEmojiHappy className="h-6 w-6 text-yellow-500" />
              <h3 className="text-lg font-bold text-gray-900">How was your order?</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">We’d love to hear your feedback. It helps us improve and lets others know what to expect.</p>
            
            <div className="flex items-center gap-1 mb-4">
              {[1,2,3,4,5].map(star => (
                <button
                  key={star}
                  onClick={() => handleRating(star)}
                  className={`p-1 rounded-full transition ${review.rating >= star ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400`}
                >
                  <HiStar className="h-7 w-7" />
                </button>
              ))}
            </div>

            <textarea
              value={review.comment}
              onChange={(e) => setReview({ ...review, comment: e.target.value })}
              placeholder="Tell us about your experience (optional)"
              rows={3}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-gray-900 focus:ring-2 focus:ring-gray-900/5 outline-none mb-4"
            />

            <button
              onClick={handleReviewSubmit}
              className="w-full bg-gray-900 text-white py-3 rounded-xl font-semibold text-sm hover:bg-black transition"
            >
              Submit Review
            </button>
          </div>
        )}

        {reviewSubmitted && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center shadow-sm">
            <p className="text-green-800 font-semibold">Thanks for your review! We appreciate your feedback. 🙏</p>
          </div>
        )}
      </div>
    </div>
  );
}